import React, { useState, useEffect, useCallback } from 'react';
import MetaTags from "react-meta-tags";
import ApiClient from '../BaseUrl/ApiClient';
import { Link, useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { Card, CardHeader, CardBody, Col, Container, Row, Input, FormGroup } from 'reactstrap';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
    };
};

function UserList() {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        totalPages: 1,
        totalDocs: 0,
        limit: 10,
    });
    const [searchQuery, setSearchQuery] = useState('');
    const history = useHistory();

    const [totalUsers, setTotalUsers] = useState(0);

    const fetchUsers = async (page = 1, limit = pagination.limit, query = searchQuery) => {
        setIsLoading(true);
        try {
            const params = { page, limit };
            if (query) params.query = query;

            const response = await ApiClient().get('/admin/user/list', { params });
            const { responseData } = response.data;

            setUsers(responseData.users);
            setPagination(responseData.pagination);
        } catch (error) {
            toast.error("Failed to fetch users.");
            if (error.response?.status === 401) {
                localStorage.removeItem("accessToken");
                localStorage.removeItem("refreshToken");
                localStorage.removeItem("authUser");
                window.location.href = "/login";
            }
        } finally {
            setIsLoading(false);
        }
    };

    const debouncedFetchUsers = useCallback(
        debounce((page, limit, query) => {
            fetchUsers(page, limit, query);
        }, 500),
        [pagination.limit]
    );

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleSearchInputChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        debouncedFetchUsers(1, pagination.limit, query);
    };

    const handlePageChange = (newPage) => {
        fetchUsers(newPage);
    };

    const handleLimitChange = (e) => {
        const newLimit = Number(e.target.value);
        setPagination(prev => ({ ...prev, limit: newLimit }));
        fetchUsers(1, newLimit);
    };



    return (
        <div className="page-content">
            <Container fluid className="mt-5">
                <MetaTags>
                    <title>General Users List</title>
                </MetaTags>
                <ToastContainer />
                <div className="auth-page-content custom-auth-page">
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader className="d-flex justify-content-between align-items-center">
                                    <h2 className="card-title mb-0">General Users</h2>
                                    <h2 className="card-title mb-0"> Total General Users: {' '}
                                        <span className="counter-value">
                                            {pagination.totalDocs}
                                        </span>
                                    </h2>
                                </CardHeader>

                                <CardBody>
                                    <div id="userList">

                                        <Row className="g-3 justify-content-end">
                                          
                                            {/* Right column for search */}
                                            <Col md={4}>
                                                   <FormGroup>
                                                <div className="search-box ms-2 position-relative">
                                                    <Input
                                                        type="text"
                                                        className="form-control search"
                                                        placeholder="Search by name, email"
                                                        value={searchQuery}
                                                        onChange={handleSearchInputChange}
                                                    />
                                                    <i className="ri-search-line search-icon"></i>
                                                    {searchQuery && (
                                                        <i
                                                            className="ri-close-line clear-icon"
                                                            style={{
                                                                position: "absolute",
                                                                right: "10px",
                                                                top: "50%",
                                                                transform: "translateY(-50%)",
                                                                cursor: "pointer",
                                                                fontSize: "18px",
                                                                color: "#666",
                                                            }}
                                                            onClick={() => {
                                                                setSearchQuery("");
                                                                debouncedFetchUsers(1, pagination.limit, "");
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                </FormGroup>
                                            </Col>
                                        </Row>


                                        <div className="table-responsive table-card mt-3 mb-1">
                                            <table className="table align-middle table-nowrap" id="userTable">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th className="sort" data-sort="name">Name</th>
                                                        <th className="sort" data-sort="email">Email</th>

                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {users.map((user) => (
                                                        <tr key={user.id}>
                                                            <td className="name">{user.name || 'N/A'}</td>
                                                            <td className="email">{user.email || 'N/A'}</td>

                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>

                                            {isLoading && users.length === 0 ? (
                                                <div className="text-center py-4">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    <h5 className="mt-2">Loading Results...</h5>
                                                </div>
                                            ) : users.length === 0 && (
                                                <div className="noresult">
                                                    <div className="text-center">
                                                        <lord-icon
                                                            src="https://cdn.lordicon.com/msoeawqm.json"
                                                            trigger="loop"
                                                            colors="primary:#121331,secondary:#08a88a"
                                                            style={{ width: '75px', height: '75px' }}
                                                        ></lord-icon>
                                                        <h5 className="mt-2">Sorry! No Result Found</h5>
                                                        <p className="text-muted mb-0">
                                                            We've searched for users but did not find any matching results.
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {pagination.totalPages > 1 && (
                                            <div className="d-flex justify-content-end">
                                                <div className="pagination-wrap hstack gap-2">
                                                    <Link
                                                        className={`page-item pagination-prev ${pagination.page === 1 ? 'disabled' : ''}`}
                                                        to="#"
                                                        onClick={() => handlePageChange(pagination.page - 1)}
                                                    >
                                                        Previous
                                                    </Link>
                                                    <ul className="pagination listjs-pagination mb-0">
                                                        {Array.from({ length: pagination.totalPages }, (_, i) => (
                                                            <li key={i} className={`page-item ${pagination.page === i + 1 ? 'active' : ''}`}>
                                                                <Link className="page-link" to="#" onClick={() => handlePageChange(i + 1)}>
                                                                    {i + 1}
                                                                </Link>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    <Link
                                                        className={`page-item pagination-next ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}
                                                        to="#"
                                                        onClick={() => handlePageChange(pagination.page + 1)}
                                                    >
                                                        Next
                                                    </Link>
                                                </div>

                                                <select
                                                    className="form-select ms-2"
                                                    style={{ width: '100px' }}
                                                    value={pagination.limit}
                                                    onChange={handleLimitChange}
                                                >
                                                    <option value="10">10</option>
                                                    <option value="20">20</option>
                                                    <option value="50">50</option>
                                                </select>
                                            </div>
                                        )}
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </Container>
        </div>
    );
}

export default UserList;